import { Button } from 'antd';
import SelectWithInfiniteScroller from 'components/atoms/SelectWithInfiniteScroller';
import ErrorBox from 'components/molecules/ErrorBox/ErrorBox';
import Loader from 'components/molecules/Loader';
import { CustomSelect } from 'components/molecules/PricingSimulator/DimSection';
import { MaxTagComponent } from 'components/molecules/PricingSimulator/Modals/CreateEditProjectContainer';
import SearchBox from 'components/molecules/SearchBox';
import { timePeriodDropdownList } from 'components/molecules/TradePromoOptimization/PlanActions';
import PlanComparisonTableContainer from 'components/molecules/TradePromoOptimization/PlanComparisonTableContainer';
import React, { useContext, useEffect, useRef, useState } from 'react';
import { DropdownItem } from 'types/tradePromoOptimization';
import { SettingOutlined, ProjectOutlined } from '@ant-design/icons';
import PlanComparisonSetupModal from 'components/molecules/TradePromoOptimization/Modals/PlanComparisonSetupModal';
import KPISelector from 'components/molecules/TradePromoOptimization/AddEventModal/PromoSections/KPISelector';
import { TPODataContext } from 'contexts/TPOContext';
import { tradePromoOptimizationUrls } from 'config/urls';
import { useCommonMutationHandler } from 'hooks/general/useCommonMutationHandler';
import { useCommonApiHandler } from 'hooks/general/useCommonApiHandler';
import { queryClient } from 'services/reactQueryService';
import { planComparisonTableParser } from 'parser/tradePromoOptimization/plansParser';
import { UserContext } from 'contexts/ConfigContext';
import { getConfig } from 'utils/helperMethods';
import dayjs from 'dayjs';
import DownloadExcel from 'components/molecules/MicroIntractions/DownloadExcel';
const dropdownWidth = 200;

const PlanComparisonContainer = () => {
    const [searchKey, setSearchKey] = useState<string>('');
    const [selectedTimePeriod, setSelectedTimePeriod] = useState(timePeriodDropdownList?.[0]);
    const [openComparisonSetupModal, setOpenComparisonSetupModal] = useState(false);
    const [isKpiFilterVisible, setIsKpiFilterVisible] = useState(false);
    const [kpiFilterDropdownValues, setKpiDropdownValues] = useState<any[]>([]);
    const [selectedPlanIds, setSelectedPlanIds] = useState<number[]>([]);
    const [comparisonTableVariable, setComparisonTableVariable] = useState<any>({
        time_period: timePeriodDropdownList?.[0]?.value,
    });

    const filtersToDisplay = useRef<any>({});

    const { kpiConfigData } = useContext(TPODataContext);
    const { appConfig } = useContext(UserContext);
    const commonConfig = getConfig('commonConfig', appConfig);
    //called when clicked on search icon or enter
    const onSearch = (val: string) => {
        // setPromoLibraryTableVariable({ ...promoLibraryTableVariable, offset: 0, search_key: val });
    };

    useEffect(() => {
        const list = Object.keys(kpiConfigData ?? {});
        const dropdownList = list?.map((configKey) => {
            return {
                label: kpiConfigData?.[configKey]?.['label'],
                value: configKey,
                isSelected: true,
            };
        });
        setKpiDropdownValues(dropdownList);
        setComparisonTableVariable({ ...comparisonTableVariable, current_date: dayjs().unix(), kpi_list: list });
    }, [kpiConfigData]);

    //comparison table data
    const {
        data: comparisonTableData,
        loading: comparisonTableDataLoading,
        error: comparisonTableDataError,
        count: comparisonTableDataCount,
    } = useCommonApiHandler({
        method: 'post',
        queryConfig: {
            enabled: Boolean('plan_ids' in comparisonTableVariable && comparisonTableVariable.plan_ids?.length > 0),
        },
        queryKey: ['tpo-comparison-data-table', comparisonTableVariable],
        url: `${tradePromoOptimizationUrls.getPlansComparisonTableDataUrl}`,
        variables: { ...comparisonTableVariable },
        parserFunction: (res: any[]) => {
            if (res?.length === 0) {
                throw 'No Data';
            }
            const { result, exportData, exportHeader } = planComparisonTableParser(res, kpiConfigData, commonConfig);
            return { result, exportData, exportHeader };
        },
    });

    return (
        <div>
            <div className="flex mt-12 justify-between items-center">
                <div className="flex gap-4 items-center">
                    <Button
                        onClick={() => {
                            setOpenComparisonSetupModal(true);
                        }}
                        className="!text-[13px]"
                        style={{ borderRadius: 6 }}
                        icon={<SettingOutlined style={{ position: 'relative', top: -3 }} />}
                    >
                        Plan Comparison Setup
                    </Button>
                </div>
                <div className="flex items-end gap-2">
                    <div>
                        <p className="mb-1 font-xs  text-fontGreyAlt ">Time Period</p>
                        <CustomSelect
                            placeholder="Time Period"
                            className="dropdown-height dropdown-width "
                            showArrow
                            loading={false}
                            value={selectedTimePeriod?.value}
                            disabled={false}
                            options={timePeriodDropdownList}
                            onChange={(val: any, item: any) => {
                                setSelectedTimePeriod(item);
                                setComparisonTableVariable({
                                    ...comparisonTableVariable,
                                    current_date: dayjs().unix(),
                                    time_period: val,
                                });
                            }}
                        />
                    </div>
                    <KPISelector
                        buttonComponent={
                            <Button
                                onClick={() => {
                                    setIsKpiFilterVisible(!isKpiFilterVisible);
                                }}
                                icon={<ProjectOutlined style={{ position: 'relative', top: -3 }} />}
                                style={{ borderRadius: 4, margin: 0 }}
                                type="default"
                            >
                                KPI
                            </Button>
                        }
                        isVisible={isKpiFilterVisible}
                        setIsVisible={setIsKpiFilterVisible}
                        kpiList={kpiFilterDropdownValues}
                        onSubmit={(values) => {
                            const updatedList = kpiFilterDropdownValues?.map((item) => {
                                return {
                                    ...item,
                                    isSelected: values.includes(item?.value),
                                };
                            });
                            setComparisonTableVariable({
                                ...comparisonTableVariable,
                                current_date: dayjs().unix(),
                                kpi_list: values,
                            });
                            setKpiDropdownValues(updatedList);
                        }}
                        variableName={'KPI'}
                        hideSearch={true}
                    />
                </div>
            </div>
            <div className="flex items-center justify-between my-2">
                <div className="flex items-center font-xs text-fontGreyAlt gap-4">
                    {Object.keys(filtersToDisplay.current ?? {}).map((item: any) => {
                        return (
                            <p>
                                {item}: {filtersToDisplay.current[item]}
                            </p>
                        );
                    })}
                </div>
                {comparisonTableData?.exportData && (
                    <DownloadExcel
                        title={'PlanComparisonExportData'}
                        titleArray={[[`Time Period: ${selectedTimePeriod?.label}`]]}
                        excelData={comparisonTableData?.exportData ?? []}
                        headers={comparisonTableData?.exportHeader ?? []}
                    />
                )}
            </div>

            <PlanComparisonTableContainer
                arePlansSelected={selectedPlanIds.length > 0}
                data={comparisonTableData?.result}
                loading={comparisonTableDataLoading}
                error={comparisonTableDataError}
            />
            <PlanComparisonSetupModal
                applyComparisonSetupLoading={comparisonTableDataLoading}
                disabled={false}
                open={openComparisonSetupModal}
                onCancel={() => {
                    setOpenComparisonSetupModal(false);
                }}
                data={{ selectedPlanIds }}
                onSubmitFinish={(plansList: number[], filters: any) => {
                    queryClient.removeQueries({
                        queryKey: ['tpo-comparison-data-table'],
                    });
                    filtersToDisplay.current = filters;
                    setSelectedPlanIds(plansList);
                    setComparisonTableVariable({
                        ...comparisonTableVariable,
                        current_date: dayjs().unix(),
                        plan_ids: plansList,
                    });
                    setOpenComparisonSetupModal(false);
                }}
            />
        </div>
    );
};

export default PlanComparisonContainer;
